import { ModalController } from "@theme/modal-controller";

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    
    // Country to language mapping 
    this.countryLanguageMap = {
      'DE': 'de',      // Germany → German
      'AT': 'de',      // Austria → German  
      'CH': 'de',      // Switzerland → German
      'FR': 'fr',      // France → French
      'BE': 'fr',      // Belgium → French
      'CA': 'fr',      // Canada → French
      'ES': 'es',      // Spain → Spanish
      'MX': 'es',      // Mexico → Spanish
      'AR': 'es',      // Argentina → Spanish
      'IT': 'it',      // Italy → Italian
      'BR': 'pt',      // Brazil → Portuguese
      'PT': 'pt',      // Portugal → Portuguese
      'NL': 'nl',      // Netherlands → Dutch
      'JP': 'ja',      // Japan → Japanese
      'CN': 'zh',      // China → Chinese
      'KR': 'ko',      // South Korea → Korean
      'RU': 'ru',      // Russia → Russian
      'PL': 'pl',      // Poland → Polish
      'SE': 'sv',      // Sweden → Swedish
      'NO': 'no',      // Norway → Norwegian  
      'DK': 'da',      // Denmark → Danish
      'FI': 'fi',      // Finland → Finnish
    };
    
    this.elements = {
      input: this.querySelector('input[name="language_code"], input[name="country_code"]'),
      button: this.querySelector("button"),
      panel: this.querySelector("ul"),
    };
    this.elements.button.addEventListener("click", this.openSelector.bind(this));
    this.elements.button.addEventListener("focusout", this.closeSelector.bind(this));
    this.addEventListener("keyup", this.onContainerKeyUp.bind(this));
    this.querySelectorAll("a").forEach((item) =>
      item.addEventListener("click", this.onItemClick.bind(this)),
    );

    // Check for pending language update after country change
    this.checkForPendingLanguageUpdate();

    if (!sessionStorage.getItem("localization_form_submitted")) {
      this.detectLocation();
    }
  }
  hidePanel() {
    this.elements.button.setAttribute("aria-expanded", "false");
    this.elements.panel.setAttribute("hidden", true);
  }
  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;
    this.hidePanel();
    this.elements.button.focus();
  }
  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector("form");
    const selectedValue = event.currentTarget.dataset.value;
    this.elements.input.value = selectedValue;
    
    // Check if this is a country selector
    const isCountrySelector = this.elements.input.name === "country_code";
    
    if (isCountrySelector) {
      // Store the selected country for language update after page reload
      sessionStorage.setItem("pending_country_change", selectedValue);
    }
    
    if (form) {
      form.submit();
      sessionStorage.setItem("localization_form_submitted", "true");
    }
  }

  /**
   * Checks if there's a pending language update after a country change
   */
  checkForPendingLanguageUpdate() {
    const pendingCountry = sessionStorage.getItem("pending_country_change");
    if (!pendingCountry) return;

    // Check if this is a language form (not country form)
    const isLanguageForm = this.elements.input && this.elements.input.name === "language_code";
    if (!isLanguageForm) return;

    // Get the target language for the country
    const targetLanguage = this.countryLanguageMap[pendingCountry];
    if (!targetLanguage) {
      // Clean up if no mapping exists
      sessionStorage.removeItem("pending_country_change");
      return;
    }

    // Check if target language is available
    const languageOption = this.querySelector(`a[data-value="${targetLanguage}"]`);
    if (!languageOption) {
      // Clean up if language not available
      sessionStorage.removeItem("pending_country_change");
      return;
    }

    // Clean up the pending flag
    sessionStorage.removeItem("pending_country_change");

    // Auto-submit the language form with a small delay to ensure DOM is ready
    setTimeout(() => {
      this.autoSubmitLanguageForm(targetLanguage);
    }, 100);
  }

  /**
   * Automatically submits the language form
   * @param {string} languageCode - The language code to submit
   */
  autoSubmitLanguageForm(languageCode) {
    const form = this.querySelector("form");
    if (!form) return;

    // Update the language input value
    this.elements.input.value = languageCode;
    
    // Submit the form
    form.submit();
    sessionStorage.setItem("localization_form_submitted", "true");
  }
  openSelector() {
    this.elements.button.focus();
    this.elements.panel.toggleAttribute("hidden");
    this.elements.button.setAttribute(
      "aria-expanded",
      (this.elements.button.getAttribute("aria-expanded") === "false").toString(),
    );
  }
  closeSelector(event) {
    const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === "BUTTON";
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }
  async detectLocation() {
    try {
      const response = await fetch(
        window.Shopify.routes.root +
          "browsing_context_suggestions.json" +
          "?country[enabled]=true" +
          `&country[exclude]=${window.Shopify.country}` +
          "&language[enabled]=true" +
          `&language[exclude]=${window.Shopify.language}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.suggestions && data.suggestions.length > 0) {
        const suggestion = data.suggestions[0];
        const currentCountry =
          window.Theme?.localization?.country?.iso_code || window.Shopify.country;

        if (
          suggestion.parts.country?.handle === currentCountry ||
          suggestion.parts.country?.name === window.Theme?.localization?.country?.name
        ) {
          return;
        }

        const modal = document.querySelector('[data-modal="location-selector-id"]');

        if (!modal) {
          console.warn("Location selector modal not found in DOM");
          return;
        }

        const modalController = new ModalController();
        modalController.presentModal("location-selector-id");
      }
    } catch (error) {
      console.error("Error fetching browsing context suggestions:", error);
    }
  }
}
customElements.define("localization-form", LocalizationForm);
